import React, { useEffect, useState } from 'react'
import { Collapse, Tooltip } from '@mui/material'
import { ExpandLessOutlined, ExpandMoreOutlined } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../generic/systemValues/systemValueLabel'

const EM_DASH = '—'
const KPIS_COLLAPSED_STORAGE_KEY = 'monitoring.byGroup.kpisCollapsed'

// Zero-state used until the first snapshot tick arrives. The KPIs ride the consolidated
// monitoring snapshot and are passed in as `kpis` — there is no standalone plant-KPI fetch.
const EMPTY_KPIS = {
  totalAssets: 0,
  workingCount: 0,
  idleCount: 0,
  errorCount: 0,
  nonOperativeCount: 0,
  criticalAlarmsCount: 0
}

/**
 * PlantKpiBar — the Stitch dashboard's "KPI Strip" (stitch-dashboard.html), 1:1:
 * flat control-room tiles (surface-container-high, hairline border, uppercase
 * micro-label + big mono number), accent colour per bucket and the "Critical Alarms"
 * tile highlighted with the red severity rail + pulsing dot.
 *
 * Collapsible (PO): a header toggle folds the tiles away; the collapsed/expanded
 * choice is remembered per user in localStorage, like the card-density toggle.
 *
 * RN1/RN2/RN5 unchanged: values come exclusively from the periodic tick (embedded in
 * the consolidated snapshot and passed in as `kpis`) and never react to cell/filter
 * navigation. P3/S12: when the snapshot omits the KPIs (degraded) every value renders
 * as an em-dash — the tile layout never breaks and the rest of the screen is unaffected.
 */
export default function PlantKpiBar ({ kpis = EMPTY_KPIS }) {
  const { t_ } = useSmartTranslation()
  const degraded = kpis?.degraded === true

  const [collapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem(KPIS_COLLAPSED_STORAGE_KEY) === 'true' } catch { return false }
  })
  useEffect(() => {
    try { window.localStorage.setItem(KPIS_COLLAPSED_STORAGE_KEY, String(collapsed)) } catch { /* storage unavailable — non-fatal */ }
  }, [collapsed])

  const format = (value) => (degraded ? EM_DASH : String(value ?? EM_DASH))

  // PO (2026-07-07): state KPI titles are the LITERAL state names (WORKING/IDLE/
  // ERROR/OFF, untranslated) so they read exactly like the chips and quick filters.
  // NON_OPERATIVE is a device MODE, not a status — the fourth visible STATUS is OFF;
  // the sun DTO field stays `nonOperativeCount`, only the label changes.
  const tiles = [
    { key: 'totalAssets', label: t_('Total assets'), value: kpis?.totalAssets, modifier: '' },
    { key: 'workingCount', label: systemValueLabel(t_, 'DeviceStatusEnum', 'WORKING'), value: kpis?.workingCount, modifier: 'mcx-kpi--running' },
    { key: 'idleCount', label: systemValueLabel(t_, 'DeviceStatusEnum', 'IDLE'), value: kpis?.idleCount, modifier: 'mcx-kpi--idle' },
    { key: 'errorCount', label: systemValueLabel(t_, 'DeviceStatusEnum', 'ERROR'), value: kpis?.errorCount, modifier: 'mcx-kpi--error' },
    { key: 'nonOperativeCount', label: systemValueLabel(t_, 'DeviceStatusEnum', 'OFF'), value: kpis?.nonOperativeCount, modifier: 'mcx-kpi--muted' }
  ]

  return (
    <section className='mcx-kpipanel' data-testid='plant-kpi-bar' data-degraded={String(degraded)} data-collapsed={String(collapsed)}>
      <button
        type='button'
        data-testid='plant-kpi-toggle'
        className='mcx-kpipanel__head'
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((current) => !current)}
      >
        <span className='mcx-eyebrow'>{t_('Plant KPIs')}</span>
        {/* Collapsed: keep the critical-alarms count visible as a compact hint. */}
        {collapsed && !degraded && (kpis?.criticalAlarmsCount ?? 0) > 0 && (
          <span className='mcx-kpipanel__hint'>
            <span className='mcx-dot mcx-pulse' style={{ background: 'var(--mcx-error)' }} />
            {format(kpis?.criticalAlarmsCount)} {t_('Critical alarms')}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <Tooltip title={collapsed ? t_('Expand') : t_('Collapse')}>
          {collapsed ? <ExpandMoreOutlined sx={{ fontSize: 18, color: 'var(--mcx-on-variant)' }} /> : <ExpandLessOutlined sx={{ fontSize: 18, color: 'var(--mcx-on-variant)' }} />}
        </Tooltip>
      </button>

      <Collapse in={!collapsed}>
        <div className='mcx-kpis'>
          {tiles.map((tile) => (
            <div key={tile.key} className={`mcx-kpi ${tile.modifier}`.trim()} data-testid={`plant-kpi-${tile.key}`}>
              <span className='mcx-kpi__label'>{tile.label}</span>
              <span className='mcx-kpi__value'>{format(tile.value)}</span>
            </div>
          ))}
          <div className='mcx-kpi mcx-kpi--critical' data-testid='plant-kpi-criticalAlarmsCount'>
            <span className='mcx-kpi__label'>{t_('Critical alarms')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {!degraded && (kpis?.criticalAlarmsCount ?? 0) > 0 && (
                <span className='mcx-dot mcx-pulse' style={{ background: 'var(--mcx-error)' }} />
              )}
              <span className='mcx-kpi__value'>{format(kpis?.criticalAlarmsCount)}</span>
            </span>
          </div>
        </div>
      </Collapse>
    </section>
  )
}
